import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Sparkles, FileSignature, Loader2, Download, Copy, Upload, FileText, Send, ShieldCheck, Check, History } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["proposal", "agreement", "engagement_letter", "ppp", "investor_deck", "memo"];
const STATUSES = ["draft", "review", "sent", "signed", "rejected"];

const STATUS_COLOR = {
  draft: "#94A3B8", review: "#F59E0B", sent: "#3B82F6",
  signed: "#22C55E", rejected: "#EF4444",
};

const fmtMoney = (n) => {
  if (!n) return "—";
  if (n >= 1_000_000) return `GH₵ ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `GH₵ ${(n / 1_000).toFixed(0)}K`;
  return `GH₵ ${n}`;
};

export default function Proposals() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [openGen, setOpenGen] = useState(false);
  const [genForm, setGenForm] = useState({ type: "proposal", value: 0 });
  const [generating, setGenerating] = useState(false);
  const [exportMenu, setExportMenu] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareForm, setShareForm] = useState({ signer_name: "", signer_email: "" });
  const [shareLink, setShareLink] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const fileInputRef = useRef(null);

  const refresh = async () => {
    const r = await api.get("/proposals");
    setItems(r.data);
    if (!active && r.data[0]) setActive(r.data[0]);
  };

  useEffect(() => { refresh(); }, []);

  const updateStatus = async (status) => {
    const r = await api.put(`/proposals/${active.id}`, { status });
    setActive(r.data);
    refresh();
  };

  const generate = async () => {
    if (!genForm.client_name || !genForm.objective) {
      toast.error("Client and objective are required.");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post("/proposals/generate", genForm);
      setActive(r.data); refresh();
      setOpenGen(false); setGenForm({ type: "proposal", value: 0 });
      toast.success("Document generated by Nexus AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(active?.content || "");
    toast.success("Copied to clipboard");
  };

  const download = () => {
    const blob = new Blob([active?.content || ""], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active.title.replace(/\s/g, "_")}_v${active.version}.md`;
    a.click();
  };

  const exportAs = async (fmt) => {
    setExportMenu(false);
    try {
      const r = await api.get(`/proposals/${active.id}/export?format=${fmt}`, { responseType: "blob" });
      const blob = new Blob([r.data], {
        type: fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.title.replace(/\s/g, "_")}_v${active.version}.${fmt}`;
      a.click();
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name.replace(/\.[^.]+$/, ""));
    fd.append("type", "proposal");
    try {
      const r = await api.post("/proposals/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setActive(r.data); refresh();
      toast.success("Document imported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
  };

  const sendForSignature = async () => {
    if (!shareForm.signer_email || !shareForm.signer_email.includes("@")) {
      toast.error("Signer email is required");
      return;
    }
    setSharing(true);
    try {
      const r = await api.post(`/proposals/${active.id}/share-for-signature`, {
        ...shareForm,
        origin_url: window.location.origin,
      });
      setShareLink(r.data);
      refresh();
      toast.success("Signing link generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create signing link");
    } finally { setSharing(false); }
  };

  const loadAudit = async () => {
    try {
      const r = await api.get(`/proposals/${active.id}/signatures`);
      setAudit(r.data);
      setAuditOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load audit trail");
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="proposals-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Institutional Engine</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Proposals & Documents</h1>
          <p className="text-slate-400 mt-2 text-sm">AI-generated institutional documents with version control.</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="file" ref={fileInputRef} accept=".pdf,.docx,.txt,.md" className="hidden" onChange={importFile} data-testid="import-file-input" />
          <button
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-proposal-btn"
            className="px-4 py-2 flex items-center gap-2 font-medium text-sm border border-white/10 hover:border-[#D4AF37]/50 text-slate-300 hover:text-white"
          >
            <Upload className="w-4 h-4" /> Import
          </button>
          <button
            onClick={() => setOpenGen(true)}
            data-testid="generate-proposal-btn"
            className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            <Sparkles className="w-4 h-4" /> Generate with AI
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* List */}
        <div className="col-span-12 lg:col-span-4 space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map((p) => (
            <motion.div
              layout
              key={p.id}
              onClick={() => setActive(p)}
              data-testid={`proposal-${p.id}`}
              className={`p-4 cursor-pointer transition-all border ${active?.id === p.id ? "border-[#D4AF37]/50 bg-[#10182B]" : "border-white/10 hover:border-[#D4AF37]/30 bg-[#0A111F]"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <FileSignature className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} />
                <span className="label-eyebrow">{p.type.replace(/_/g, " ")}</span>
                <span className="font-mono text-[10px] ml-auto" style={{ color: STATUS_COLOR[p.status] }}>{p.status.toUpperCase()}</span>
              </div>
              <div className="font-display text-base text-white leading-tight">{p.title}</div>
              <div className="text-xs text-slate-500 mt-1">{p.client_name}</div>
              <div className="mt-3 flex justify-between items-center pt-3 border-t border-white/5">
                <span className="font-mono text-sm" style={{ color: "var(--gold-400)" }}>{fmtMoney(p.value)}</span>
                <span className="font-mono text-[10px] text-slate-500">v{p.version}</span>
              </div>
            </motion.div>
          ))}
          {!items.length && (
            <div className="text-center text-slate-500 text-sm py-10">No proposals yet. Generate your first one.</div>
          )}
        </div>

        {/* Viewer */}
        <div className="col-span-12 lg:col-span-8">
          {active ? (
            <div className="nexus-card">
              <div className="px-8 py-5 border-b border-white/10 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="label-eyebrow mb-1">// Pandora Management Consult Ltd</div>
                  <h2 className="font-display text-2xl text-white">{active.title}</h2>
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-xs font-mono">
                    <span className="text-slate-400 uppercase tracking-widest">{active.type.replace(/_/g, " ")}</span>
                    <span className="text-slate-700">|</span>
                    <span style={{ color: "var(--gold-400)" }}>{fmtMoney(active.value)}</span>
                    <span className="text-slate-700">|</span>
                    <span className="text-slate-400">v{active.version}</span>
                    <span className="text-slate-700">|</span>
                    <select
                      value={active.status}
                      onChange={(e) => updateStatus(e.target.value)}
                      data-testid="status-select"
                      className="bg-transparent border border-white/10 px-2 py-1 text-xs font-mono uppercase tracking-widest focus:border-[#D4AF37] outline-none"
                      style={{ color: STATUS_COLOR[active.status] }}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setShareOpen(true)} data-testid="send-for-signature-btn" title="Send for signature"
                          className="p-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-md transition-all">
                    <Send className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  {(active.status === "signed" || active.status === "sent") && (
                    <button onClick={loadAudit} data-testid="audit-btn" title="Signature audit trail"
                            className="p-2 border border-white/10 hover:border-[#00F0FF]/50 text-slate-400 hover:text-[#00F0FF] rounded-md">
                      <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                  <button onClick={copy} data-testid="copy-btn" className="p-2 border border-white/10 hover:border-[#D4AF37]/50 text-slate-400 hover:text-[#D4AF37] rounded-md"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
                  <div className="relative">
                    <button
                      onClick={() => setExportMenu(!exportMenu)}
                      data-testid="export-btn"
                      className="p-2 border border-white/10 hover:border-[#D4AF37]/50 text-slate-400 hover:text-[#D4AF37]"
                    >
                      <Download className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    {exportMenu && (
                      <div className="absolute right-0 top-full mt-1 bg-[#0A111F] border border-white/10 min-w-[160px] z-20">
                        <button onClick={() => exportAs("pdf")} data-testid="export-pdf" className="block w-full px-4 py-2 text-left text-xs font-mono tracking-widest text-slate-300 hover:bg-[#10182B] hover:text-[#D4AF37]">EXPORT PDF</button>
                        <button onClick={() => exportAs("docx")} data-testid="export-docx" className="block w-full px-4 py-2 text-left text-xs font-mono tracking-widest text-slate-300 hover:bg-[#10182B] hover:text-[#D4AF37]">EXPORT DOCX</button>
                        <button onClick={() => { setExportMenu(false); download(); }} className="block w-full px-4 py-2 text-left text-xs font-mono tracking-widest text-slate-300 hover:bg-[#10182B] hover:text-[#D4AF37]">EXPORT MD</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="p-8 max-h-[70vh] overflow-y-auto">
                <div className="nexus-prose whitespace-pre-wrap">{active.content}</div>
              </div>
            </div>
          ) : (
            <div className="nexus-card p-10 text-center text-slate-500">No proposal selected.</div>
          )}
        </div>
      </div>

      {openGen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpenGen(false)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="generate-form"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">// AI Document Engine</div>
                <h2 className="font-display text-2xl text-white">Generate Institutional Document</h2>
              </div>
              <button onClick={() => setOpenGen(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-1.5">Document type</label>
                <div className="grid grid-cols-3 gap-2">
                  {TYPES.map((t) => (
                    <button
                      key={t}
                      onClick={() => setGenForm({ ...genForm, type: t })}
                      data-testid={`type-${t}`}
                      className={`px-3 py-2 text-xs font-mono tracking-widest uppercase border ${genForm.type === t ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-slate-400 hover:border-white/30"}`}
                    >{t.replace(/_/g, " ")}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Client / Recipient *</label>
                <input
                  data-testid="gen-client"
                  value={genForm.client_name || ""}
                  onChange={(e) => setGenForm({ ...genForm, client_name: e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
                  placeholder="e.g., Federal Ministry of Agriculture"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Objective *</label>
                <textarea
                  data-testid="gen-objective"
                  rows={2}
                  value={genForm.objective || ""}
                  onChange={(e) => setGenForm({ ...genForm, objective: e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none resize-none"
                  placeholder="What is this document seeking to achieve?"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Scope (optional)</label>
                <textarea
                  rows={3}
                  value={genForm.scope || ""}
                  onChange={(e) => setGenForm({ ...genForm, scope: e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none resize-none"
                  placeholder="Brief scope or key inputs (workstreams, deliverables…)"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Indicative value (GH₵)</label>
                <input
                  type="number"
                  value={genForm.value || ""}
                  onChange={(e) => setGenForm({ ...genForm, value: +e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpenGen(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
              <button
                onClick={generate}
                disabled={generating}
                data-testid="gen-submit-btn"
                className="px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-60"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generating ? "Drafting…" : "Generate"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Send for signature modal */}
      {shareOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => { setShareOpen(false); setShareLink(null); }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="nexus-glass-panel p-8 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            <button onClick={() => { setShareOpen(false); setShareLink(null); }} className="absolute top-4 right-4 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            <div className="label-eyebrow mb-1">// Secure Signing</div>
            <h2 className="font-display text-2xl text-white">Send for signature</h2>
            <p className="text-slate-400 text-sm mt-1">Mint a single-use 14-day signing link for <span className="text-white">{active?.title}</span>.</p>
            {!shareLink ? (
              <div className="mt-6 space-y-4">
                <div>
                  <label className="label-eyebrow block mb-1.5">Signer name (optional)</label>
                  <input value={shareForm.signer_name} onChange={(e) => setShareForm({ ...shareForm, signer_name: e.target.value })}
                         className="nexus-input" data-testid="share-signer-name" placeholder="e.g., Hon. Abena Adjei" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1.5">Signer email *</label>
                  <input value={shareForm.signer_email} onChange={(e) => setShareForm({ ...shareForm, signer_email: e.target.value })}
                         className="nexus-input" data-testid="share-signer-email" placeholder="signer@company.com" />
                </div>
                <div className="text-[11px] text-slate-500 leading-relaxed">
                  The signer will open the link directly — no Pandora Nexus account required. We capture their IP, timestamp,
                  and a SHA-256 hash binding the signature to the proposal content.
                </div>
                <div className="flex justify-end gap-3 mt-4">
                  <button onClick={() => setShareOpen(false)} className="px-5 py-2 text-slate-300 border border-white/10 rounded-md">Cancel</button>
                  <button onClick={sendForSignature} disabled={sharing} data-testid="share-submit"
                          className="px-5 py-2 rounded-md font-medium disabled:opacity-50 flex items-center gap-2"
                          style={{ background: "linear-gradient(135deg, #FCE588 0%, #D4AF37 100%)", color: "#050B14" }}>
                    {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Generate signing link
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <div className="p-4 rounded-xl bg-emerald-500/8 border border-emerald-500/30">
                  <div className="flex items-center gap-2 text-emerald-400 mb-2"><Check className="w-4 h-4" /> Signing link ready</div>
                  <div className="font-mono text-[12px] text-white break-all" data-testid="signing-link">{shareLink.url}</div>
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(shareLink.url); toast.success("Link copied"); }}
                  data-testid="copy-link-btn"
                  className="mt-3 w-full py-2.5 rounded-md flex items-center justify-center gap-2 border border-white/10 hover:border-[#D4AF37]/40 text-white text-sm"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy link
                </button>
                <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                  Send this link directly to <span className="text-white">{shareLink.signer_email}</span>.
                  Link expires {new Date(shareLink.expires_at).toLocaleString()}.
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Audit trail modal */}
      {auditOpen && audit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setAuditOpen(false)}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="nexus-glass-panel p-8 w-full max-w-2xl mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="audit-modal">
            <button onClick={() => setAuditOpen(false)} className="absolute top-4 right-4 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            <div className="label-eyebrow mb-1 flex items-center gap-2"><History className="w-3 h-3" /> Audit trail</div>
            <h2 className="font-display text-2xl text-white">Signature record</h2>

            <div className="mt-6">
              <div className="label-eyebrow mb-2">// Signatures captured ({audit.signatures.length})</div>
              {audit.signatures.length === 0 && <div className="text-slate-500 text-sm">No signatures yet.</div>}
              <div className="space-y-3">
                {audit.signatures.map((s) => (
                  <div key={s.id} className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
                    <div className="flex items-center justify-between">
                      <div className="font-display text-white text-base">{s.signer_name}</div>
                      <div className="text-[10px] text-emerald-400 font-mono tracking-widest">SIGNED</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3 text-[12px]">
                      <div><span className="text-slate-500">Email: </span><span className="text-white">{s.signer_email}</span></div>
                      <div><span className="text-slate-500">Title: </span><span className="text-white">{s.signer_title || "—"}</span></div>
                      <div><span className="text-slate-500">IP: </span><span className="text-white font-mono">{s.ip || "—"}</span></div>
                      <div><span className="text-slate-500">When: </span><span className="text-white">{new Date(s.signed_at).toLocaleString()}</span></div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <div className="label-eyebrow mb-1">SHA-256 Content Hash</div>
                      <div className="font-mono text-[10px] text-slate-400 break-all">{s.content_hash}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <div className="label-eyebrow mb-2">// Signing links issued ({audit.tokens.length})</div>
              <div className="space-y-2">
                {audit.tokens.map((t) => (
                  <div key={t.id} className="p-3 rounded-xl border border-white/10 bg-white/[0.02]">
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="text-white">{t.signer_email}</span>
                      <span className="font-mono text-[10px]" style={{ color: t.used ? "#22C55E" : "#F59E0B" }}>
                        {t.used ? "USED" : "PENDING"}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono mt-1">
                      Issued {new Date(t.created_at).toLocaleString()} · Expires {new Date(t.expires_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
